'use client';

import { useEffect } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Shadcn } from '@/components/ui/scope';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

/**
 * Next.js App Router error boundary. Mounted around the segment when a
 * thrown error escapes a Server / Client Component below this layout.
 * Must be a Client Component and accept `{ error, reset }`.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Best-effort logging; in prod this would forward to Sentry/Rollbar.
    console.error('[app/error] uncaught error-:', error);
  }, [error]);

  return (
    <Shadcn className="flex min-h-screen items-center justify-center bg-background p-3">
      <Card className="w-full max-w-[520px] p-6 text-center sm:p-10">
        <div className="flex flex-col items-center gap-4">
          <span className="flex h-16 w-16 items-center justify-center rounded-md bg-warning/10 text-warning">
            <AlertTriangle className="h-8 w-8" />
          </span>
          <h1 className="text-[20px] font-extrabold tracking-[-0.02em] text-foreground sm:text-[24px]">
            Something went wrong
          </h1>
          <p className="max-w-[420px] text-sm text-muted-foreground">
            {error?.message || 'An unexpected error occurred. You can retry, or refresh the page.'}
          </p>
          {error?.digest && (
            <p className="text-[11px] text-muted-foreground" style={{ fontFamily: 'var(--font-mono), monospace' }}>
              ref: {error.digest}
            </p>
          )}

          <div className="mt-2 flex w-full flex-col justify-center gap-3 sm:flex-row">
            <Button onClick={() => reset()} className="font-bold">
              <RefreshCw className="h-4 w-4" />
              Try again
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (typeof window !== 'undefined') window.location.reload();
              }}
              className="font-semibold"
            >
              Reload page
            </Button>
          </div>
        </div>
      </Card>
    </Shadcn>
  );
}
